// src/pages/TicketRecall/components/TicketRecallList.tsx
// Bảng danh sách phiếu thu hồi vé ế + action Confirm/Cancel cho phiếu còn Nháp (mục 13.3) — bản
// rút gọn của TicketDeliveryList.tsx. Cột "Nguồn" hiển thị Kho tổng hoặc tên đại lý (agentId) tuỳ
// theo phiếu là thu hồi từ kho tổng hay từ 1 đại lý cụ thể.

import React from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_RECALL_STATUS,
  TICKET_RECALL_STATUS_COLORS,
  TICKET_RECALL_STATUS_LABELS,
  TicketRecall,
  TicketRecallPagination,
} from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

interface TicketRecallListProps {
  recalls: TicketRecall[];
  pagination: TicketRecallPagination;
  agentMap: AgentMap;
  loading: boolean;
  onViewDetail: (recall: TicketRecall) => void;
  onConfirm: (recall: TicketRecall) => void;
  onCancel: (recall: TicketRecall) => void;
}

const RECALL_ACTION_COLUMN_CODES = ['button-detail', 'button-confirm', 'button-cancel'];

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

// Phần HH bán bị ĐẢO do thu hồi số vé ế này = giá trị quy đổi × % HH SNAPSHOT lúc lập phiếu thu hồi / 100.
const salesCommissionClawbackOf = (r: TicketRecall): number | null =>
  typeof r.salesCommissionRate === 'number' && typeof r.estimatedValue === 'number'
    ? Math.round(r.estimatedValue * r.salesCommissionRate / 100)
    : null;

const TicketRecallList: React.FC<TicketRecallListProps> = ({
  recalls,
  pagination,
  agentMap,
  loading,
  onViewDetail,
  onConfirm,
  onCancel,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    RECALL_ACTION_COLUMN_CODES,
  );

  const columnCount = (canShowActionColumn ? 9 : 8) + 1;

  const renderSource = (recall: TicketRecall) =>
    recall.agentId ? (
      <span className="badge bg-primary-subtle text-primary">{getAgentName(agentMap, recall.agentId)}</span>
    ) : (
      <span className="badge bg-secondary-subtle text-secondary">{st('kho_tong_text')}</span>
    );

  const renderStatusBadge = (recall: TicketRecall) => {
    const color =
      recall.statusBadgeColor || TICKET_RECALL_STATUS_COLORS[String(recall.status)] || 'secondary';
    const label =
      recall.statusName || TICKET_RECALL_STATUS_LABELS[String(recall.status)] || st('khong_ro_text');
    return <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>{label}</span>;
  };

  const renderActions = (recall: TicketRecall) => {
    const isDraft = Number(recall.status) === TICKET_RECALL_STATUS.Draft;

    return (
      <div className="d-flex justify-content-center gap-1 flex-nowrap">
        {PageActionPermission.canDisplay(location.pathname, 'button-detail') && (
          <Button
            color="info"
            outline
            size="sm"
            className="btn-icon"
            title={st('xem_chi_tiet_title')}
            data-action-code="button-detail"
            data-action-name="Xem chi tiết phiếu thu hồi vé ế"
            onClick={() => onViewDetail(recall)}
          >
            <i className="ri-eye-line"></i>
          </Button>
        )}

        {isDraft && PageActionPermission.canDisplay(location.pathname, 'button-confirm') && (
          <Button
            color="success"
            outline
            size="sm"
            className="btn-icon"
            title={st('xac_nhan_thu_hoi_title')}
            data-action-code="button-confirm"
            data-action-name="Xác nhận thu hồi vé ế"
            onClick={() => onConfirm(recall)}
          >
            <i className="ri-check-line"></i>
          </Button>
        )}

        {isDraft && PageActionPermission.canDisplay(location.pathname, 'button-cancel') && (
          <Button
            color="danger"
            outline
            size="sm"
            className="btn-icon"
            title={st('huy_phieu_title')}
            data-action-code="button-cancel"
            data-action-name="Huỷ phiếu thu hồi vé ế"
            onClick={() => onCancel(recall)}
          >
            <i className="ri-close-line"></i>
          </Button>
        )}
      </div>
    );
  };

  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
          <th>{st('ma_phieu_column')}</th>
          <th>{st('ke_hoach_phat_hanh_column')}</th>
          <th>{st('nguon_column')}</th>
          <th className="text-end">{st('so_luong_ve_column')}</th>
          <th className="text-end">{st('hh_ban_dao_column')}</th>
          <th>{st('trang_thai_column')}</th>
          <th>{st('nguoi_lap_column')}</th>
          <th>{st('ngay_lap_column')}</th>
          {canShowActionColumn && (
            <th style={{ width: 150 }} className="text-center">
              {st('thao_tac_column')}
            </th>
          )}
        </tr>
      }
    >
      {loading ? (
        Array.from({ length: 5 }).map((_, row) => (
          <tr key={row}>
            {Array.from({ length: columnCount }).map((__, col) => (
              <td key={col}>
                <div className="placeholder-glow">
                  <span className="placeholder col-8"></span>
                </div>
              </td>
            ))}
          </tr>
        ))
      ) : recalls.length === 0 ? (
        <tr>
          <td colSpan={columnCount} className="text-center text-muted py-5">
            <div className="mb-2">
              <i className="ri-file-list-3-line fs-1 text-muted"></i>
            </div>
            {st('chua_co_phieu_thu_hoi_ve_text')}
          </td>
        </tr>
      ) : (
        recalls.map((recall, index) => (
          <tr key={recall.id}>
            <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
            <td>
              <div className="fw-medium text-primary">{recall.recallCode}</div>
            </td>
            <td>{recall.planCode || recall.issuancePlanId}</td>
            <td>{renderSource(recall)}</td>
            <td className="text-end">{formatNumber(recall.totalQuantity)}</td>
            <td className="text-end">
              {typeof recall.salesCommissionRate === 'number' ? (
                <>
                  {formatCurrency(salesCommissionClawbackOf(recall))}
                  <small className="text-muted d-block">{recall.salesCommissionRate}%</small>
                </>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td>{renderStatusBadge(recall)}</td>
            <td>{recall.createdByName || '-'}</td>
            <td>{formatDate(recall.createdAt)}</td>
            {canShowActionColumn && <td className="text-center">{renderActions(recall)}</td>}
          </tr>
        ))
      )}
    </DataTableShell>
  );
};

export default TicketRecallList;
